/**
 * PivotPoints.jsx — dashboard/PivotPoints.jsx
 *
 * Classic (floor) pivot points calculated from the previous completed daily bar.
 * Formula: P = (H + L + C) / 3, R1/S1/R2/S2/R3/S3 derived from P.
 *
 * Uses bars[length-2] not bars[length-1] because the last bar is today's
 * in-progress candle during market hours — we want yesterday's completed OHLC.
 *
 * Levels nearest to current price are highlighted bold.
 * The P (pivot) level spans the full grid width.
 */

import { pivots } from '../../lib/indicators';
import { fmtPrice } from '../../lib/format';

export default function PivotPoints({ bars, currentPrice, symbol }) {
  if (!bars || bars.length < 2) {
    return <div className="empty">Loading pivots…</div>;
  }

  // Previous completed bar
  const prev = bars[bars.length - 2];
  const p    = pivots(prev.h, prev.l, prev.c);
  if (!p) return <div className="empty">Insufficient bar data.</div>;

  const date = new Date(prev.t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  const levels = [
    { k: 'R3', v: p.R3, cls: 'r' },
    { k: 'R2', v: p.R2, cls: 'r' },
    { k: 'R1', v: p.R1, cls: 'r' },
    { k: 'P',  v: p.P,  cls: 'p', full: true }, // spans both columns
    { k: 'S1', v: p.S1, cls: 's' },
    { k: 'S2', v: p.S2, cls: 's' },
    { k: 'S3', v: p.S3, cls: 's' },
  ];

  return (
    <div>
      <div className="stat-row" style={{ borderBottom: '1px solid var(--border)' }}>
        <span className="stat-lbl">Pivots · {symbol}</span>
        <span className="faint mono" style={{ fontSize: 10 }}>from {date}</span>
      </div>

      <div className="pivot-grid">
        {levels.map(({ k, v, cls, full }) => {
          // Highlight any level within 0.25% of current price
          const isNear = currentPrice != null && Math.abs(currentPrice - v) / v < 0.0025;
          return (
            <div
              key={k}
              className={`pivot-cell ${cls}${isNear ? ' near' : ''}`}
              style={{ gridColumn: full ? '1/-1' : undefined }}
            >
              <span className="pk">{k}</span>
              <span>{fmtPrice(v)}</span>
            </div>
          );
        })}
      </div>

      {currentPrice != null && (
        <div className="stat-row">
          <span className="stat-lbl">Current</span>
          <span className="stat-val">{fmtPrice(currentPrice)}</span>
        </div>
      )}
    </div>
  );
}
